import React, { useState, useMemo } from 'react';
import { View, StyleSheet, FlatList, Pressable, ActivityIndicator, Text, Image } from 'react-native';
import { Dialog, YStack, XStack, Card } from 'tamagui';
import { useQuery } from '@tanstack/react-query';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';

interface MediaSelectorProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string, imageData?: any) => void;
}

const MediaSelector: React.FC<MediaSelectorProps> = ({ visible, onClose, onSelect }) => {
  const theme = themeSelector();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fetch media library
  const { data, isLoading, error } = useQuery({
    queryKey: ['mediaLibrary'],
    queryFn: async () => {
      try {
        const result = await apis.getGlobalAssets(true);
        if (!Array.isArray(result)) {
          return [];
        }
        return result;
      } catch (error) {
        console.error('Error fetching media library:', error);
        return [];
      }
    },
    enabled: visible,
  });

  // Filter to only images
  const imageItems = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) => {
      const url = typeof item === 'string' ? item : item?.url;
      if (!url) return false;
      const extension = url.toLowerCase().split('.').pop();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      return imageExtensions.includes(extension || '');
    });
  }, [data]);

  const handleSelect = (item: any) => {
    const url = typeof item === 'string' ? item : item?.url;
    if (url) {
      onSelect(url, item);
      onClose();
    }
  };

  const renderItem = ({ item, index }: { item: any; index: number }) => {
    const imageUrl = typeof item === 'string' ? item : item?.url;
    const title = typeof item === 'string'
      ? item.substring(item.lastIndexOf('/') + 1)
      : item?.name || item?.title || 'Image';

    return (
      <Pressable
        onPress={() => handleSelect(item)}
        style={({ pressed }) => [
          styles.imageItem,
          {
            backgroundColor: pressed
              ? (theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(96, 165, 250, 0.2)')
              : (theme ? '#f8fafc' : '#1f2937'),
          },
        ]}
      >
        <Image
          source={{ uri: imageUrl }}
          style={styles.imageThumbnail}
          resizeMode="cover"
        />
        <Text
          style={[
            styles.imageTitle,
            { color: theme ? '#1f2937' : '#f9fafb' },
          ]}
          numberOfLines={1}
        >
          {title}
        </Text>
      </Pressable>
    );
  };

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
          }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          style={{
            padding: 0,
            minWidth: 600,
            maxWidth: 800,
            maxHeight: '80%',
            borderRadius: 24,
            overflow: 'hidden',
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
          }}
        >
          {/* Header */}
          <View
            style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}
          >
            <XStack alignItems="center" justifyContent="space-between">
              <XStack alignItems="center" gap="$3">
                <View
                  style={{
                    backgroundColor: theme ? '#f0f9ff' : '#0c4a6e',
                    borderRadius: 12,
                    padding: 10,
                  }}
                >
                  <MaterialIcons
                    name="photo-library"
                    size={20}
                    color={theme ? '#0ea5e9' : '#38bdf8'}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title
                    style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4,
                      margin: 0,
                    }}
                  >
                    Select Image
                  </Dialog.Title>
                  <Dialog.Description
                    style={{
                      fontSize: 14,
                      color: theme ? '#64748b' : '#94a3b8',
                      fontWeight: '500',
                      margin: 0,
                    }}
                  >
                    Choose an image from your media library
                  </Dialog.Description>
                </YStack>
              </XStack>
              <Pressable
                onPress={onClose}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: theme ? '#F3F4F6' : '#4B5563',
                }}
              >
                <AntDesign name="close" size={20} color={theme ? '#1F2937' : '#F9FAFB'} />
              </Pressable>
            </XStack>
          </View>

          {/* Content */}
          <View
            style={{
              padding: 24,
              backgroundColor: theme ? '#ffffff' : '#1e293b',
              flex: 1,
            }}
          >
            {isLoading && (
              <YStack justifyContent="center" alignItems="center" flex={1} gap="$3">
                <ActivityIndicator
                  size="large"
                  color={theme ? '#3b82f6' : '#60a5fa'}
                />
                <Text style={{ color: theme ? '#64748b' : '#94a3b8' }}>
                  Loading images...
                </Text>
              </YStack>
            )}

            {error && (
              <YStack justifyContent="center" alignItems="center" flex={1}>
                <MaterialIcons
                  name="error-outline"
                  size={48}
                  color={theme ? '#ef4444' : '#f87171'}
                />
                <Text
                  style={{
                    color: theme ? '#ef4444' : '#f87171',
                    marginTop: 12,
                    fontSize: 14,
                  }}
                >
                  Error loading images
                </Text>
              </YStack>
            )}

            {!isLoading && !error && imageItems.length === 0 && (
              <YStack justifyContent="center" alignItems="center" flex={1}>
                <MaterialIcons
                  name="photo-library"
                  size={48}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
                <Text
                  style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    marginTop: 12,
                    fontSize: 14,
                  }}
                >
                  No images available
                </Text>
              </YStack>
            )}

            {!isLoading && !error && imageItems.length > 0 && (
              <FlatList
                data={imageItems}
                renderItem={renderItem}
                numColumns={3}
                keyExtractor={(item, index) => {
                  const url = typeof item === 'string' ? item : item?.url || '';
                  return `media-${index}-${url}`;
                }}
                contentContainerStyle={styles.listContent}
                columnWrapperStyle={styles.row}
              />
            )}
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

const styles = StyleSheet.create({
  listContent: {
    padding: 8,
  },
  row: {
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  imageItem: {
    width: '30%',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  imageThumbnail: {
    width: '100%',
    aspectRatio: 1,
  },
  imageTitle: {
    fontSize: 12,
    padding: 8,
    textAlign: 'center',
  },
});

export default MediaSelector;
















